import { css } from '@emotion/react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import { CircularProgress, IconButton, Tooltip } from '@mui/material';
import { CellClassParams, ColDef, ICellRendererParams } from 'ag-grid-community';
import { FC, useMemo, useState } from 'react';
import {
  ConfirmDialog,
  ConfirmDialogTitleOptions,
} from '../../../components/Dialog/ConfirmDialog/ConfirmDialog';
import { AgTable } from '../../../components/AgTable/AgTable';
import { FileTypeIcon } from '../../../components/FileTypeIcon/FileTypeIcon';
import {
  DealDocDisplayStatus,
  deriveDealDocDisplayStatus,
  IRoundDocumentMetadata,
} from '../../../data-models/round-document.data-model';
import { useDeleteRoundDocumentById } from '../../../services/queries-hooks/MaggieRoundDocumentQueriesHooks';
import { getRoundDocumentDownloadUrlById } from '../../../services/queries/MaggieRoundDocumentQueries';
import { colors } from '../../../theme/colors';
import { AgFormatterService } from '../../../util/ag-formatter-service';
import {
  gridActionsDividerCss,
  gridAutoCss,
  gridHeaderEllipsisCss,
  gridHugRowsCss,
  gridOverlayMutedCss,
} from './deals-detail.styles';

export interface IRoundDocumentRow {
  doc: IRoundDocumentMetadata;
  fileName: string;
  status: DealDocDisplayStatus;
  uploadedAt: string | null;
  size: string | null;
}

/** Grid rows for the round's documents, sorted by file name (case-insensitive). */
export function toDocumentRows(documents: IRoundDocumentMetadata[]): IRoundDocumentRow[] {
  return [...documents]
    .sort((a, b) => a.fileName.toLowerCase().localeCompare(b.fileName.toLowerCase()))
    .map((doc) => ({
      doc,
      fileName: doc.fileName,
      status: deriveDealDocDisplayStatus(doc),
      uploadedAt: doc.createdAt || null,
      size: doc.fileSize || null,
    }));
}

const STATUS_COLORS: Record<DealDocDisplayStatus, string> = {
  Uploaded: colors.text.main,
  Processing: colors.text.secondary,
  Extracted: colors.success[60],
  Failed: colors.critical[50],
  'Incomplete Set': colors.warning[60],
};

const fileCellCss = css`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 100%;
  min-width: 0;
`;

const fileNameLinkCss = css`
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  color: var(--mui-palette-secondary-main);
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;

  &:hover {
    text-decoration: underline;
  }

  &:disabled {
    cursor: default;
    text-decoration: none;
  }
`;

const actionsCellCss = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
  height: 100%;
`;

interface RoundDocumentsGridProps {
  roundId: number;
  documents: IRoundDocumentMetadata[];
  canEdit?: boolean;
}

/**
 * Documents grid for the Deals-tab detail pane: typed file icon and name (click
 * to download), extraction-pipeline status, upload time, size, and row actions.
 */
export const RoundDocumentsGrid: FC<RoundDocumentsGridProps> = ({ roundId, documents, canEdit = true }) => {
  const deleteMutation = useDeleteRoundDocumentById();
  const [downloadingDocId, setDownloadingDocId] = useState<number | null>(null);
  const [deletingDocId, setDeletingDocId] = useState<number | null>(null);
  const [documentToDelete, setDocumentToDelete] = useState<IRoundDocumentMetadata | null>(null);

  const rows = useMemo(() => toDocumentRows(documents), [documents]);

  const handleDownload = async (doc: IRoundDocumentMetadata) => {
    try {
      setDownloadingDocId(doc.id);
      const { downloadUrl } = await getRoundDocumentDownloadUrlById(roundId, doc.id);
      // Anchor element with the download attribute avoids popup blockers.
      const link = window.document.createElement('a');
      link.href = downloadUrl;
      link.download = doc.fileName;
      window.document.body.appendChild(link);
      try {
        link.click();
      } finally {
        window.document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Failed to download document:', error);
    } finally {
      setDownloadingDocId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!documentToDelete) {
      return;
    }
    setDeletingDocId(documentToDelete.id);
    setDocumentToDelete(null);
    try {
      await deleteMutation.mutateAsync({ roundId, documentId: documentToDelete.id });
    } catch (error) {
      console.error('Failed to delete document:', error);
    } finally {
      setDeletingDocId(null);
    }
  };

  const columnDefs = useMemo<ColDef<IRoundDocumentRow>[]>(
    () => [
      {
        headerName: 'File Name',
        field: 'fileName',
        flex: 3,
        minWidth: 240,
        headerTooltip: 'File Name',
        cellRenderer: ({ data }: ICellRendererParams<IRoundDocumentRow>) =>
          data ? (
            <span css={fileCellCss}>
              <FileTypeIcon fileName={data.fileName} fontSize='small' sx={{ flexShrink: 0 }} />
              <button
                type='button'
                css={fileNameLinkCss}
                title={data.fileName}
                disabled={downloadingDocId === data.doc.id}
                onClick={() => handleDownload(data.doc)}
              >
                {data.fileName}
              </button>
            </span>
          ) : null,
      },
      {
        headerName: 'Status',
        field: 'status',
        flex: 1,
        minWidth: 110,
        headerTooltip: 'Status',
        cellStyle: ({ value }: CellClassParams<IRoundDocumentRow>) => ({
          color: STATUS_COLORS[value as DealDocDisplayStatus] ?? colors.text.main,
        }),
      },
      {
        headerName: 'Uploaded At',
        field: 'uploadedAt',
        flex: 1.2,
        minWidth: 170,
        headerTooltip: 'Uploaded At',
        // Wall-clock time in the viewer's zone, the way the Document Hub shows it.
        valueFormatter: AgFormatterService.get().getFormatterForId('dateTimeShort'),
      },
      { headerName: 'Size', field: 'size', flex: 0.7, minWidth: 90, headerTooltip: 'Size' },
      {
        headerName: '',
        colId: 'actions',
        width: canEdit ? 90 : 56,
        maxWidth: canEdit ? 90 : 56,
        sortable: false,
        resizable: false,
        filter: false,
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
        cellRenderer: ({ data }: ICellRendererParams<IRoundDocumentRow>) => {
          if (!data) {
            return null;
          }
          const isDownloading = downloadingDocId === data.doc.id;
          const isDeleting = deletingDocId === data.doc.id;
          return (
            <div css={actionsCellCss}>
              <Tooltip title='Download'>
                <span>
                  <IconButton
                    size='small'
                    onClick={() => handleDownload(data.doc)}
                    disabled={isDownloading || isDeleting}
                  >
                    {isDownloading ? <CircularProgress size={16} /> : <DownloadIcon fontSize='small' />}
                  </IconButton>
                </span>
              </Tooltip>
              {canEdit && (
                <Tooltip title='Remove'>
                  <span>
                    <IconButton
                      size='small'
                      color='error'
                      onClick={() => setDocumentToDelete(data.doc)}
                      disabled={isDeleting}
                    >
                      {isDeleting ? <CircularProgress size={16} /> : <DeleteOutlineIcon fontSize='small' />}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canEdit, downloadingDocId, deletingDocId, roundId]
  );

  return (
    <>
      <div
        css={[
          gridAutoCss,
          gridHeaderEllipsisCss,
          gridOverlayMutedCss,
          gridActionsDividerCss,
          rows.length > 0 && gridHugRowsCss,
        ]}
      >
        <AgTable
          columnDefs={columnDefs}
          rowData={rows}
          domLayout='autoHeight'
          defaultColDef={{
            flex: 1,
            suppressSizeToFit: false,
            wrapHeaderText: false,
            autoHeaderHeight: false,
          }}
          rowGroupPanelShow='never'
          overlayNoRowsTemplate='No documents uploaded for this round.'
        />
      </div>
      <ConfirmDialog
        open={documentToDelete != null}
        onClose={() => setDocumentToDelete(null)}
        onConfirm={handleConfirmDelete}
        title='Delete Document'
        type={ConfirmDialogTitleOptions.Warning}
        loading={deleteMutation.isPending}
      >
        Are you sure you want to delete &quot;{documentToDelete?.fileName}&quot;? This action cannot be
        undone.
      </ConfirmDialog>
    </>
  );
};
